import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import {
  STAFF_ROLE_HELP,
  STAFF_ROLE_LABEL,
  type StaffAccount,
  type StaffRole,
} from '../api/types';
import { IconPlus, IconShield } from '../components/icons';
import { Pill } from '../components/license';
import {
  Button,
  Callout,
  Dialog,
  EmptyState,
  ErrorNote,
  SelectField,
  SkeletonRows,
  TextField,
  useToast,
} from '../components/ui';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/license';

const ROLES: StaffRole[] = ['admin', 'deployment', 'support'];

/**
 * Staff accounts (admin only). The three console roles are handed out here, so
 * the page itself is reachable only by an admin — the route guard and the
 * server both enforce that.
 */
export function StaffPage() {
  const [creating, setCreating] = useState(false);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['staff-accounts'],
    queryFn: () => api.get<{ staff: StaffAccount[] }>('/api/staff/staff'),
  });

  const staff = data?.staff ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Staff</h1>
          <p className="page-head__sub">
            The people who sign in to this console, and what each may do. Deactivating an account
            takes effect on its next request; there is no delete, so the audit trail stays intact.
          </p>
        </div>
        <div className="page-head__actions">
          <Button variant="primary" icon={<IconPlus />} onClick={() => setCreating(true)}>
            Add staff
          </Button>
        </div>
      </div>

      <section className="panel">
        {isPending && <SkeletonRows rows={3} />}
        {isError && (
          <div className="panel__body">
            <ErrorNote error={error} />
          </div>
        )}

        {!isPending && !isError && staff.length === 0 && (
          <EmptyState
            icon={<IconShield size={30} />}
            title="No staff accounts"
            body="Add the first console account and give it a role."
          />
        )}

        {staff.length > 0 && (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                  <th className="table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((account) => (
                  <StaffRow key={account.id} account={account} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AddStaffDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function StaffRow({ account }: { account: StaffAccount }) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { staff: me } = useAuth();
  const isSelf = me?.id === account.id;

  const update = useMutation({
    mutationFn: (patch: { role?: StaffRole; isActive?: boolean }) =>
      api.patch(`/api/staff/staff/${account.id}`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff-accounts'] });
      push({ tone: 'success', title: `${account.name} updated` });
    },
  });

  return (
    <tr>
      <td>
        <div className="cell-stack">
          <span className="cell-strong">{account.name}</span>
          {isSelf && <span className="cell-meta">You</span>}
        </div>
      </td>
      <td className="cell-mono">{account.email}</td>
      <td>
        <select
          className="select"
          aria-label={`Role for ${account.name}`}
          value={account.role}
          // You cannot change your own role away from admin (lock-out guard).
          disabled={update.isPending || (isSelf && account.role === 'admin')}
          onChange={(event) => update.mutate({ role: event.currentTarget.value as StaffRole })}
        >
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {STAFF_ROLE_LABEL[role]}
            </option>
          ))}
        </select>
      </td>
      <td>
        {account.is_active ? <Pill tone="live">Active</Pill> : <Pill tone="expired">Off</Pill>}
      </td>
      <td className="cell-meta tabular">
        {account.last_login_at ? formatDate(account.last_login_at) : '—'}
      </td>
      <td className="table__actions">
        <Button
          size="sm"
          variant={account.is_active ? 'danger' : undefined}
          loading={update.isPending}
          disabled={isSelf}
          title={isSelf ? 'You cannot deactivate your own account.' : undefined}
          onClick={() => update.mutate({ isActive: !account.is_active })}
        >
          {account.is_active ? 'Deactivate' : 'Reactivate'}
        </Button>
        {update.isError && <ErrorNote error={update.error} />}
      </td>
    </tr>
  );
}

function AddStaffDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('support');
  const [password, setPassword] = useState('');

  const reset = () => {
    setName('');
    setEmail('');
    setRole('support');
    setPassword('');
  };

  const create = useMutation({
    mutationFn: () => api.post('/api/staff/staff', { name, email, role, password }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff-accounts'] });
      push({ tone: 'success', title: `${name} added` });
      reset();
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        create.reset();
        onClose();
      }}
      title="Add staff"
      hint="They sign in with this email and password. Ask them to change the password after their first sign-in."
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        {create.isError && <ErrorNote error={create.error} />}

        <TextField label="Name" value={name} required onChange={(e) => setName(e.currentTarget.value)} />
        <TextField
          label="Email"
          type="email"
          value={email}
          required
          autoComplete="off"
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <SelectField
          label="Role"
          value={role}
          hint={STAFF_ROLE_HELP[role]}
          onChange={(e) => setRole(e.currentTarget.value as StaffRole)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {STAFF_ROLE_LABEL[r]}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Temporary password"
          type="password"
          value={password}
          required
          autoComplete="new-password"
          hint="At least 10 characters."
          onChange={(e) => setPassword(e.currentTarget.value)}
        />

        <Callout tone="info">
          The role decides what they can do; the server enforces it on every request, not just in
          the menus they see.
        </Callout>

        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Add staff
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
